import BaseService from '@/services/BaseService';
export type ObligationScheduleSubmission = {
  osId: number;
  cncSummaryId: number;
  category: string;
  categoryLabel: string;
  submissionDate: string | null;
  parentEntityId: number;
  canDownloadZip: boolean;
};

export type ObligationScheduleSubmissionsByOsId = Record<string, ObligationScheduleSubmission[]>;

export type ZipStatusResponse = {
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';
  percentage: number;
  downloadUrl?: string;
  error?: string;
};

const SUBMISSIONS_LOOKUP_PATH = 'reportingobligations/schedules/submissions/lookup';

function throwIfResponseError(response: Response): never {
  const detail = response.statusText ? `${response.status} ${response.statusText}` : String(response.status);
  throw new Error(`Funding request failed (${detail})`);
}

function normalizeSubmission(raw: Record<string, unknown>): ObligationScheduleSubmission | null {
  const cncSummaryId = raw.cncSummaryId ?? raw.CncSummaryId;
  const osId = raw.osId ?? raw.OsId;
  if (typeof cncSummaryId !== 'number' || typeof osId !== 'number') {
    return null;
  }
  return {
    osId,
    cncSummaryId,
    category: String(raw.category ?? raw.Category ?? ''),
    categoryLabel: String(raw.categoryLabel ?? raw.CategoryLabel ?? ''),
    submissionDate:
      raw.submissionDate != null
        ? String(raw.submissionDate)
        : raw.SubmissionDate != null
          ? String(raw.SubmissionDate)
          : null,
    parentEntityId: Number(raw.parentEntityId ?? raw.ParentEntityId ?? 0),
    canDownloadZip: Boolean(raw.canDownloadZip ?? raw.CanDownloadZip),
  };
}

function normalizeSubmissionsMap(body: Record<string, unknown>): ObligationScheduleSubmissionsByOsId {
  const rawMap = (body.submissionsByOsId ?? body.SubmissionsByOsId) as Record<string, unknown> | undefined;
  if (!rawMap || typeof rawMap !== 'object') {
    return {};
  }

  const result: ObligationScheduleSubmissionsByOsId = {};
  for (const [key, value] of Object.entries(rawMap)) {
    if (!Array.isArray(value)) {
      continue;
    }
    result[key] = value
      .map(item => (item && typeof item === 'object' ? normalizeSubmission(item as Record<string, unknown>) : null))
      .filter((item): item is ObligationScheduleSubmission => item != null);
  }
  return result;
}

export class ObligationScheduleSubmissionsService extends BaseService {
  constructor() {
    super('/api/funding-service');
  }

  fetchObligationScheduleSubmissions = async (osIds: number[]): Promise<ObligationScheduleSubmissionsByOsId> => {
    if (!osIds.length) {
      return {};
    }

    const raw = await this.post<{ osIds: number[] }, unknown>({
      path: SUBMISSIONS_LOOKUP_PATH,
      body: { osIds },
    } as never);

    if (raw instanceof Response) {
      throwIfResponseError(raw);
    }
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return normalizeSubmissionsMap(raw as Record<string, unknown>);
    }
    return {};
  };

  startCncSummaryZipDownload = async (cncSummaryId: number): Promise<{ processId: string }> => {
    const raw = await this.post<undefined, { processId: string } | Response>({
      path: `cncsummary/${cncSummaryId}/zip`,
    } as never);

    if (raw instanceof Response) {
      throwIfResponseError(raw);
    }
    if (!raw || typeof raw !== 'object' || !('processId' in raw)) {
      throw new Error('Unexpected zip start response');
    }
    return raw as { processId: string };
  };

  checkCncSummaryZipStatus = async (cncSummaryId: number, processId: string): Promise<ZipStatusResponse> => {
    const raw = await this.get<ZipStatusResponse | Response>({
      path: `zip/${cncSummaryId}/${processId}/status`,
    } as never);

    if (raw instanceof Response) {
      throwIfResponseError(raw);
    }
    return raw as ZipStatusResponse;
  };
}
